import { cs } from '../../../lib/currency';
import React, { useMemo, useCallback, memo } from 'react';
import { Button } from '../../../components/ui/button';
import { Checkbox } from '../../../components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../../../components/ui/dropdown-menu';
import {
  Check, X, Pause, PlayCircle, Ban, Pencil, MoreHorizontal, History,
  ArrowRightLeft, ChevronDown, ChevronUp, RefreshCw, CalendarClock, Undo2,
} from 'lucide-react';
import { formatDateOnly } from '../../../utils/timezone';

// ============================================================
// EnrollmentTable Component - DEFINED OUTSIDE to prevent re-creation on parent re-render
// ============================================================
const EnrollmentTable = memo(({
  data,
  showActions = false,
  showDates = false,
  actionType = 'pending',
  sortField,
  sortDirection,
  onSort,
  visibleColumns,
  selectedEnrollments = [],
  onToggleSelection,
  onSelectAll,
  onApprove,
  onReject,
  onEdit,
  onMove,
  onPause,
  onCancel,
  onResume,
  onCancelPause,
  onEditPause,
  onHistory,
  onRefund,
  onRecalculateBilling,
}) => {
  // iter307b — Sort is now done by the parent BEFORE pagination
  // (see Enrollments.js → sortEnrollments util) so each page receives
  // the correct slice of an already-sorted global list. Previously
  // the sort lived here, but EnrollmentTable only sees one page at
  // a time, so each page sorted independently and the global order
  // was broken on every page change.
  const sortedData = data;

  const handleSortClick = useCallback((field) => {
    if (onSort) {
      onSort(field);
    }
  }, [onSort]);

  // Render sortable header (function form to avoid nested-component lint)
  const sortableHeader = (field, label, className = "") => (
    <TableHead
      className={`cursor-pointer hover:bg-slate-100 select-none ${className}`}
      onClick={() => handleSortClick(field)}
      data-testid={`sort-${field}`}
    >
      <div className="flex items-center gap-1">
        {label}
        {sortField === field && (
          sortDirection === 'asc'
            ? <ChevronUp className="h-4 w-4 text-blue-600" />
            : <ChevronDown className="h-4 w-4 text-blue-600" />
        )}
        {sortField !== field && <ChevronDown className="h-4 w-4 text-slate-300" />}
      </div>
    </TableHead>
  );

  return (
  <Table>
    <TableHeader>
      <TableRow className="bg-slate-50">
        {visibleColumns.select && (
          <TableHead className="w-10">
            <Checkbox
              checked={sortedData.length > 0 && sortedData.every(e => selectedEnrollments?.includes(e.id))}
              onCheckedChange={() => onSelectAll?.(sortedData)}
            />
          </TableHead>
        )}
        {visibleColumns.enrollmentId && sortableHeader("numeric_id", "ID")}
        {visibleColumns.player && sortableHeader("player_name", "Player")}
        {visibleColumns.playerId && sortableHeader("player_numeric_id", "Player ID")}
        {visibleColumns.program && sortableHeader("program_name", "Program")}
        {visibleColumns.parent && sortableHeader("parent_name", "Parent")}
        {visibleColumns.parentId && sortableHeader("parent_numeric_id", "Parent ID")}
        {visibleColumns.parentEmail && sortableHeader("parent_email", "Parent Email")}
        {visibleColumns.parentPhone && sortableHeader("parent_phone", "Parent Phone")}
        {visibleColumns.class && sortableHeader("class_name", "Class")}
        {visibleColumns.classDay && sortableHeader("class_day", "Day")}
        {visibleColumns.classTime && sortableHeader("class_time", "Time")}
        {visibleColumns.status && sortableHeader("status", "Status")}
        {showDates && visibleColumns.startDate && (
          sortableHeader("start_date", "Class Start")
        )}
        {showDates && visibleColumns.approvedAt && (
          sortableHeader("approved_at", "Approved")
        )}
        {visibleColumns.pauseDate && sortableHeader("pause_date", "Paused")}
        {visibleColumns.autoResumeDate && sortableHeader("auto_resume_date", "Auto Resume")}
        {visibleColumns.cancelDate && sortableHeader("cancel_date", "Cancelled")}
        {visibleColumns.fee && sortableHeader("monthly_fee", "Fee")}
        {visibleColumns.frequency && sortableHeader("payment_frequency", "Frequency")}
        {visibleColumns.paymentStatus && sortableHeader("payment_status", "Pay Status")}
        {visibleColumns.paymentDueDate && sortableHeader("payment_due_date", "Due Date")}
        {visibleColumns.lastPaymentDate && sortableHeader("last_payment_date", "Last Payment")}
        {visibleColumns.nextBilling && sortableHeader("next_billing_date", "Next Billing")}
        {visibleColumns.creditBalance && sortableHeader("credit_balance", "Credit")}
        {visibleColumns.prorated && <TableHead>Prorated</TableHead>}
        {visibleColumns.proratedAmount && sortableHeader("prorated_amount", "Prorated Amt")}
        {visibleColumns.notes && <TableHead>Notes</TableHead>}
        {visibleColumns.createdAt && sortableHeader("created_at", "Created")}
        {visibleColumns.actions && <TableHead className="text-right">Actions</TableHead>}
      </TableRow>
    </TableHeader>
    <TableBody>
      {sortedData.length === 0 ? (
        <TableRow>
          <TableCell colSpan={Object.values(visibleColumns).filter(Boolean).length} className="text-center py-8 text-slate-500">
            No enrollments found matching your filters
          </TableCell>
        </TableRow>
      ) : (
      sortedData.map((enrollment) => (
        <TableRow key={enrollment.id} className={`table-row-hover ${selectedEnrollments?.includes(enrollment.id) ? 'bg-blue-50' : ''}`} data-testid={`enrollment-row-${enrollment.id}`}>
          {visibleColumns.select && (
          <TableCell>
            <Checkbox
              checked={selectedEnrollments?.includes(enrollment.id)}
              onCheckedChange={() => onToggleSelection?.(enrollment.id)}
            />
          </TableCell>
          )}
          {visibleColumns.enrollmentId && (
          <TableCell data-testid={`enrollment-id-cell-${enrollment.id}`}>
            <a
              href={`/admin/enrollments/${enrollment.id}`}
              className="font-mono text-sm text-blue-600 hover:underline"
              data-testid={`enrollment-detail-link-${enrollment.id}`}
            >
              {enrollment.numeric_id || enrollment.id.slice(0, 8)}
            </a>
          </TableCell>
          )}
          {visibleColumns.player && (
          <TableCell data-testid={`player-cell-${enrollment.id}`}>
            <a
              href={`/admin/players/${enrollment.player_id}`}
              className="font-medium text-blue-600 hover:underline"
            >
              {enrollment.player_name}
            </a>
          </TableCell>
          )}
          {visibleColumns.playerId && (
          <TableCell>
            <span className="font-mono text-xs text-slate-500">{enrollment.player_numeric_id || '-'}</span>
          </TableCell>
          )}
          {visibleColumns.program && <TableCell>{enrollment.program_name}</TableCell>}
          {visibleColumns.parent && <TableCell>{enrollment.parent_name}</TableCell>}
          {visibleColumns.parentId && (
          <TableCell>
            <span className="font-mono text-xs text-slate-500">{enrollment.parent_numeric_id || '-'}</span>
          </TableCell>
          )}
          {visibleColumns.parentEmail && (
          <TableCell>
            <span className="text-xs text-slate-600">{enrollment.parent_email || '-'}</span>
          </TableCell>
          )}
          {visibleColumns.parentPhone && (
          <TableCell>
            <span className="text-xs text-slate-600">{enrollment.parent_phone || '-'}</span>
          </TableCell>
          )}
          {visibleColumns.class && (
          <TableCell>
            {/* Show multiple class names if available */}
            {enrollment.class_names && enrollment.class_names.length > 0 ? (
              <div className="space-y-1">
                {enrollment.class_names.map((name, idx) => (
                  <p key={`${enrollment.id}-class-${name}-${idx}`} className="text-sm">{name}</p>
                ))}
              </div>
            ) : (
              enrollment.class_name || '-'
            )}
          </TableCell>
          )}
          {visibleColumns.classDay && (
          <TableCell>
            <span className="text-xs text-slate-600 capitalize">{enrollment.class_day || '-'}</span>
          </TableCell>
          )}
          {visibleColumns.classTime && (
          <TableCell>
            <span className="text-xs text-slate-600">{enrollment.class_time || '-'}</span>
          </TableCell>
          )}
          {visibleColumns.status && (
          <TableCell>
            <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${
              enrollment.status === 'current' ? 'bg-green-100 text-green-700' :
              enrollment.status === 'approved' ? 'bg-blue-100 text-blue-700' :
              enrollment.status === 'paused' ? 'bg-amber-100 text-amber-700' :
              enrollment.status === 'canceled' || enrollment.status === 'cancelled' ? 'bg-red-100 text-red-700' :
              enrollment.status === 'pending' ? 'bg-slate-100 text-slate-700' :
              'bg-slate-100 text-slate-700'
            }`}>
              {enrollment.status}
            </span>
          </TableCell>
          )}
          {showDates && visibleColumns.startDate && (
            <TableCell>
              <div className="text-xs space-y-1">
                {enrollment.start_date ? (
                  <p className="font-medium">{formatDateOnly(enrollment.start_date, { weekday: undefined, year: 'numeric', month: 'numeric', day: 'numeric' })}</p>
                ) : (
                  <span className="text-slate-400">-</span>
                )}
                {enrollment.pause_date && (
                  <p className="text-amber-600"><span className="font-medium">Paused:</span> {formatDateOnly(enrollment.pause_date, { weekday: undefined, year: 'numeric', month: 'numeric', day: 'numeric' })}</p>
                )}
                {enrollment.auto_resume_date && (
                  <p className="text-green-600"><span className="font-medium">Auto:</span> {formatDateOnly(enrollment.auto_resume_date, { weekday: undefined, year: 'numeric', month: 'numeric', day: 'numeric' })}</p>
                )}
                {enrollment.cancel_date && (
                  <p className="text-red-600"><span className="font-medium">Canceled:</span> {formatDateOnly(enrollment.cancel_date, { weekday: undefined, year: 'numeric', month: 'numeric', day: 'numeric' })}</p>
                )}
              </div>
            </TableCell>
          )}
          {showDates && visibleColumns.approvedAt && (
            <TableCell>
              {enrollment.approved_at ? (
                <span className="text-xs text-slate-600">{new Date(enrollment.approved_at).toLocaleDateString()}</span>
              ) : (
                <span className="text-xs text-slate-400">-</span>
              )}
            </TableCell>
          )}
          {visibleColumns.pauseDate && (
            <TableCell>
              {enrollment.pause_date ? (
                <span className="text-xs text-amber-600">{formatDateOnly(enrollment.pause_date, { weekday: undefined, year: 'numeric', month: 'numeric', day: 'numeric' })}</span>
              ) : (
                <span className="text-xs text-slate-400">-</span>
              )}
            </TableCell>
          )}
          {visibleColumns.autoResumeDate && (
            <TableCell>
              {enrollment.auto_resume_date ? (
                <span className="text-xs text-green-600">{formatDateOnly(enrollment.auto_resume_date, { weekday: undefined, year: 'numeric', month: 'numeric', day: 'numeric' })}</span>
              ) : (
                <span className="text-xs text-slate-400">-</span>
              )}
            </TableCell>
          )}
          {visibleColumns.cancelDate && (
            <TableCell>
              {enrollment.cancel_date ? (
                <span className="text-xs text-red-600">{formatDateOnly(enrollment.cancel_date, { weekday: undefined, year: 'numeric', month: 'numeric', day: 'numeric' })}</span>
              ) : (
                <span className="text-xs text-slate-400">-</span>
              )}
            </TableCell>
          )}
          {visibleColumns.fee && (
          <TableCell>
            {enrollment.monthly_fee ? `${cs()}${enrollment.monthly_fee.toFixed(2)}/mo` :
             enrollment.fee_amount ? `${cs()}${enrollment.fee_amount.toFixed(2)}` : '-'}
          </TableCell>
          )}
          {visibleColumns.frequency && (
          <TableCell>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
              enrollment.payment_frequency === 'quarterly' ? 'bg-purple-100 text-purple-700' :
              enrollment.payment_frequency === 'biannual' ? 'bg-indigo-100 text-indigo-700' :
              'bg-slate-100 text-slate-700'
            }`}>
              {enrollment.payment_frequency === 'quarterly' ? 'Quarterly' :
               enrollment.payment_frequency === 'biannual' ? 'Bi-Annual' :
               'Monthly'}
            </span>
          </TableCell>
          )}
          {visibleColumns.paymentStatus && (
          <TableCell>
            {enrollment.payment_status ? (
              <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${
                enrollment.payment_status === 'paid' ? 'bg-green-100 text-green-700' :
                enrollment.payment_status === 'pending' ? 'bg-amber-100 text-amber-700' :
                enrollment.payment_status === 'overdue' ? 'bg-red-100 text-red-700' :
                enrollment.payment_status === 'failed' ? 'bg-red-100 text-red-700' :
                'bg-slate-100 text-slate-700'
              }`}>
                {enrollment.payment_status}
              </span>
            ) : (
              <span className="text-xs text-slate-400">-</span>
            )}
          </TableCell>
          )}
          {visibleColumns.paymentDueDate && (
          <TableCell>
            {enrollment.payment_due_date ? (
              <span className="text-xs">{formatDateOnly(enrollment.payment_due_date, { weekday: undefined, year: 'numeric', month: 'numeric', day: 'numeric' })}</span>
            ) : (
              <span className="text-xs text-slate-400">-</span>
            )}
          </TableCell>
          )}
          {visibleColumns.lastPaymentDate && (
          <TableCell>
            {enrollment.last_payment_date ? (
              <span className="text-xs">{formatDateOnly(enrollment.last_payment_date, { weekday: undefined, year: 'numeric', month: 'numeric', day: 'numeric' })}</span>
            ) : (
              <span className="text-xs text-slate-400">-</span>
            )}
          </TableCell>
          )}
          {visibleColumns.nextBilling && (
          <TableCell>
            <div className="flex items-center gap-1">
              {enrollment.next_billing_date ? (
                <span className="text-xs">{formatDateOnly(enrollment.next_billing_date, { weekday: undefined, year: 'numeric', month: 'numeric', day: 'numeric' })}</span>
              ) : (
                <span className="text-xs text-slate-400">-</span>
              )}
              {actionType === 'active' && onRecalculateBilling && (
                <button
                  type="button"
                  onClick={() => onRecalculateBilling(enrollment)}
                  className="p-1 rounded hover:bg-blue-50 text-slate-400 hover:text-blue-600 transition-colors"
                  title="Recalculate from billing history"
                  data-testid={`recalc-billing-date-${enrollment.id}`}
                >
                  <RefreshCw className="h-3 w-3" />
                </button>
              )}
            </div>
          </TableCell>
          )}
          {visibleColumns.creditBalance && (
          <TableCell>
            {enrollment.credit_balance > 0 ? (
              <span className="text-xs font-medium text-green-600">{cs()}{enrollment.credit_balance.toFixed(2)}</span>
            ) : (
              <span className="text-xs text-slate-400">-</span>
            )}
          </TableCell>
          )}
          {visibleColumns.prorated && (
          <TableCell>
            {enrollment.is_prorated ? (
              <span className="px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">Yes</span>
            ) : (
              <span className="text-xs text-slate-400">No</span>
            )}
          </TableCell>
          )}
          {visibleColumns.proratedAmount && (
          <TableCell>
            {enrollment.prorated_amount ? (
              <span className="text-xs font-medium">{cs()}{enrollment.prorated_amount.toFixed(2)}</span>
            ) : (
              <span className="text-xs text-slate-400">-</span>
            )}
          </TableCell>
          )}
          {visibleColumns.notes && (
          <TableCell>
            {enrollment.notes ? (
              <span className="text-xs text-slate-600 max-w-[150px] truncate block" title={enrollment.notes}>
                {enrollment.notes}
              </span>
            ) : (
              <span className="text-xs text-slate-400">-</span>
            )}
          </TableCell>
          )}
          {visibleColumns.createdAt && (
          <TableCell>
            {enrollment.created_at ? (
              <span className="text-xs text-slate-600">{new Date(enrollment.created_at).toLocaleDateString()}</span>
            ) : (
              <span className="text-xs text-slate-400">-</span>
            )}
          </TableCell>
          )}
          {visibleColumns.actions && (
          <TableCell className="text-right">
            {/* Pending actions */}
            {actionType === 'pending' && (
              <div className="flex items-center justify-end gap-2">
                <Button
                  size="sm"
                  className="bg-blue-500 hover:bg-blue-600"
                  onClick={() => onApprove(enrollment)}
                  data-testid={`approve-enrollment-${enrollment.id}`}
                >
                  <Check className="h-4 w-4 mr-1" />
                  Approve
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-rose-600 border-rose-200 hover:bg-rose-50"
                  onClick={() => onReject(enrollment)}
                  data-testid={`reject-enrollment-${enrollment.id}`}
                >
                  <X className="h-4 w-4 mr-1" />
                  Reject
                </Button>
              </div>
            )}

            {/* Active enrollment actions */}
            {actionType === 'active' && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" data-testid={`actions-${enrollment.id}`}>
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => onEdit(enrollment)}>
                    <Pencil className="h-4 w-4 mr-2" />
                    Edit
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onMove(enrollment)}>
                    <ArrowRightLeft className="h-4 w-4 mr-2" />
                    Move
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onPause(enrollment)}>
                    <Pause className="h-4 w-4 mr-2" />
                    Pause
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onCancel(enrollment)} className="text-red-600">
                    <Ban className="h-4 w-4 mr-2" />
                    Cancel
                  </DropdownMenuItem>
                  {enrollment.payment_status === 'paid' && onRefund && (
                    <DropdownMenuItem
                      onClick={() => onRefund(enrollment)}
                      className="text-amber-700"
                      data-testid={`refund-enrollment-${enrollment.id}`}
                    >
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Issue Refund
                    </DropdownMenuItem>
                  )}
                  {onRecalculateBilling && (
                    <DropdownMenuItem
                      onClick={() => onRecalculateBilling(enrollment)}
                      data-testid={`recalc-billing-menu-${enrollment.id}`}
                    >
                      <CalendarClock className="h-4 w-4 mr-2" />
                      Recalculate Bill Date
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => onHistory(enrollment)}>
                    <History className="h-4 w-4 mr-2" />
                    View History
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            {/* Paused enrollment actions */}
            {actionType === 'paused' && (
              <div className="flex items-center justify-end gap-2">
                <Button
                  size="sm"
                  className="bg-green-500 hover:bg-green-600"
                  onClick={() => onResume(enrollment)}
                  data-testid={`resume-enrollment-${enrollment.id}`}
                >
                  <PlayCircle className="h-4 w-4 mr-1" />
                  Resume
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onEditPause(enrollment)}
                  data-testid={`edit-pause-${enrollment.id}`}
                >
                  <Pencil className="h-4 w-4 mr-1" />
                  Edit
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-amber-700 border-amber-200 hover:bg-amber-50"
                  onClick={() => onCancelPause?.(enrollment)}
                  data-testid={`cancel-pause-${enrollment.id}`}
                  title="Undo the pause: restores NBD and reverses pause credit"
                >
                  <Undo2 className="h-4 w-4 mr-1" />
                  Cancel Pause
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-red-600 border-red-200 hover:bg-red-50"
                  onClick={() => onCancel(enrollment)}
                  data-testid={`cancel-enrollment-${enrollment.id}`}
                >
                  <Ban className="h-4 w-4 mr-1" />
                  Cancel
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => onHistory(enrollment)}
                  data-testid={`history-enrollment-${enrollment.id}`}
                >
                  <History className="h-4 w-4" />
                </Button>
              </div>
            )}

            {/* Canceled - view history only */}
            {actionType === 'canceled' && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => onHistory(enrollment)}
                data-testid={`history-enrollment-${enrollment.id}`}
              >
                <History className="h-4 w-4" />
              </Button>
            )}
          </TableCell>
          )}
        </TableRow>
      ))
      )}
    </TableBody>
  </Table>
  );
});

EnrollmentTable.displayName = 'EnrollmentTable';

export default EnrollmentTable;
